
import React, { useCallback, useEffect, useState } from "react";
import { Prompt, useHistory, useParams, useLocation } from "react-router";

import { BeforeCapture, DragDropContext, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import toast from "react-hot-toast";
import produce from "immer";

import './Update.scss';
import { Header } from "./Components/Header";
import { Board } from "./Components/Board";
import { useUpdatePageModel } from "./UpdatePageModel";
import { ErrorBoundary, ErrorMessage, Loader } from "components";
import { AsyncStatus, DraggableType } from "enums";
import { useDisableMainContainerScroll, useLuxonTimezone } from "hooks";
import { parseDateDay } from "services/Utils";
import { UsersService } from "services";

export interface IBoardContext {
    isDragging?: boolean,
    dragType?: DraggableType,
    toastError: (err: any) => void,
    toastSuccess: (message: string) => void,
}

export const BoardContext = React.createContext<IBoardContext>(undefined);

export function Update() {
    const routeParams = useParams<{
        id: string,
    }>();
    const location = useLocation()
    const searchParams = new URLSearchParams(location.search)

    const history = useHistory()
    const { model } = useUpdatePageModel();
    const [contextVal, setContextVal] = useState<IBoardContext>(() => {
        return {
            toastError: (err) => {
                toast.error(err + '', {
                    duration: 10000,
                    position: 'top-center',
                });
            },
            toastSuccess: (message) => {
                toast.success(message + '', {
                    position: 'top-center',
                });
            }
        };
    });

    // check for new and templates
    let id: number;
    let isNew = false;
    let templateID: number = 0;
    let templateLocations: string[] = [];
    
    if (routeParams.id === 'new') {
        isNew = true;

        // template variables can only be here if update is new
        if (searchParams.has("templateID")) {
            templateID = parseInt(searchParams.get("templateID"), 10)
        }
        if (searchParams.has("locations")) {
            templateLocations = searchParams.get("locations").split(",");
        }
    } else {
        id = parseInt(routeParams.id, 10);
    }

    const { isScrollDisabled, registerForScrollDisable } = useDisableMainContainerScroll();
    useEffect(() => {
        return registerForScrollDisable('updatePage');
    }, []);

    useEffect(() => {
        const {timezone} = useLuxonTimezone(UsersService.me.timezone);
        const [startDate] = parseDateDay(searchParams.get("startDate"), timezone);

        model.load(isNew, id, templateID, templateLocations, startDate)
            .then(() => {
                // If we're creating a new update, we need to redirect to the right id after creation.
                if (isNew) {
                    history.replace(`/updates/${model.update.id}`);
                }
            });
    }, []);

    const onBeforeUnload = useCallback((e) => {
        e.preventDefault();
        e.returnValue = '';
    }, []);

    // Prevent accidental leaving the page while changing the schedule
    useEffect(() => {
        if (model.isChangingSchedule) {
            window.addEventListener('beforeunload', onBeforeUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
        }
    }, [model.isChangingSchedule])

    const onBeforeCapture = useCallback((before: BeforeCapture) => {
        const parsedId = model.parseDragDropId(before.draggableId);
        if (!parsedId.isValid) return;


        setContextVal(produce(draft => {
            draft.isDragging = true;
            draft.dragType = parsedId.type;
        }));
    }, []);

    const onDragEnd = useCallback((result: DropResult, provided: ResponderProvided) => {
        // Update board state
        setContextVal(produce(draft => {
            draft.isDragging = false;
            draft.dragType = undefined;
        }));

        // Persist the change in our model.
        // First, make sure drag wasn't canceled
        if (result.reason !== 'DROP') return;
        // Might have dragged to nowhere, in which case destination will be null
        if (!result.destination) return;

        const parsedId = model.parseDragDropId(result.draggableId);
        if (!parsedId.isValid) {
            contextVal.toastError(`Invalid draggable id: ` + result.draggableId);
            return;
        }
        if (parsedId.type === DraggableType.Stage) {
            model.moveStage(parsedId.id, result.source, result.destination)
                .catch(() => contextVal.toastError(model.apiCallError));
        } else if (parsedId.type === DraggableType.Filter) {
            model.moveFilter(parsedId.id, result.source, result.destination)
                .catch(() => contextVal.toastError(model.apiCallError));
        } else {
            contextVal.toastError(`Unsupported drag type: ` + parsedId.type);
            return;
        }
    }, []);

    return (<>
        {model.loadStatus === AsyncStatus.Loading && <Loader />}
        {model.loadStatus === AsyncStatus.Error && <ErrorMessage message={model.loadError} />}
        {model.loadStatus === AsyncStatus.Success && (<ErrorBoundary>
            <div className="update_container">
                <Header model={model} />
                <DragDropContext
                    onBeforeCapture={onBeforeCapture}
                    onDragEnd={onDragEnd}
                >
                    <BoardContext.Provider value={contextVal}>
                        <div className="update_boardContainer">
                            <Board model={model} />
                        </div>
                    </BoardContext.Provider>
                </DragDropContext>
            </div>
        </ErrorBoundary>)}
        {/* This is react-router's component to interrupt SPA navigation and possibly cancel it. */}
        <Prompt
            message={(location, action) => {
                if (model.isChangingSchedule) {
                    return 'Leave page? Changes you made may not be saved.';
                }
                // Returning "true" means don't prompt the user.
                return true;
            }}
        />
    </>);
}